import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { UsersThree, Plus, Prohibit, Buildings } from "@phosphor-icons/react";

// Admin management of dated employee -> client (optional job/site) assignments. This is the single
// authoritative source for client/job RAMS applicability. Multiple concurrent assignments allowed.
export default function WorkAssignments() {
  const [people, setPeople] = useState([]);
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [includeEnded, setIncludeEnded] = useState(false);
  const [f, setF] = useState({ profile_id: "", client_id: "", project_id: "", start_date: "", end_date: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, c, a] = await Promise.all([
        api.get("/people"),
        api.get("/workforce/clients"),
        api.get(`/workforce/assignments?include_ended=${includeEnded}`),
      ]);
      setPeople(p.data || []); setClients(c.data || []); setRows(a.data || []);
    } catch { toast.error("Failed to load workforce assignments"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeEnded]);

  const jobs = clients.find((c) => c.id === f.client_id)?.jobs || [];
  const set = (k, v) => setF((s) => ({ ...s, [k]: v, ...(k === "client_id" ? { project_id: "" } : {}) }));

  const create = async () => {
    if (!f.profile_id || !f.client_id) return toast.error("Choose an employee and a client");
    setBusy(true);
    try {
      await api.post("/workforce/assignments", f);
      toast.success("Assignment added");
      setF({ profile_id: "", client_id: "", project_id: "", start_date: "", end_date: "", note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add assignment"); }
    finally { setBusy(false); }
  };

  const end = async (id) => {
    if (!window.confirm("End this assignment? The RAMS requirement from this source stops going forward; acknowledgement history is preserved.")) return;
    try { await api.post(`/workforce/assignments/${id}/end`, {}); toast.success("Assignment ended"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const badge = (s) => ({
    active: "bg-emerald-100 text-emerald-800", upcoming: "bg-amber-100 text-amber-800",
    expired: "bg-slate-200 text-slate-600", ended: "bg-slate-200 text-slate-600",
  }[s] || "bg-slate-100 text-slate-600");

  return (
    <div className="flex flex-col gap-5 max-w-3xl" data-testid="work-assignments">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600 flex gap-2">
        <Buildings size={18} className="shrink-0 mt-0.5 text-slate-500" />
        <span>Assign employees to a <b>client</b> and optionally a specific <b>job/site</b>, with start/end dates. Client-wide RAMS reach everyone actively assigned to the client; job/site RAMS reach only that job's team. Requirements update automatically on the dates — no need to edit again. Ending an assignment stops that source; history is kept.</span>
      </div>

      <div className="rounded-xl border border-slate-200 p-4 grid sm:grid-cols-2 gap-3 bg-white">
        <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Employee
          <select data-testid="wa-person" value={f.profile_id} onChange={(e) => set("profile_id", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
            <option value="">Choose an employee…</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select></label>
        <label className="text-xs font-semibold text-slate-600">Client
          <select data-testid="wa-client" value={f.client_id} onChange={(e) => set("client_id", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
            <option value="">Choose a client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></label>
        <label className="text-xs font-semibold text-slate-600">Job / site (optional)
          <select data-testid="wa-job" value={f.project_id} onChange={(e) => set("project_id", e.target.value)} disabled={!f.client_id} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm disabled:bg-slate-50">
            <option value="">Whole client (all jobs)</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
          </select></label>
        <label className="text-xs font-semibold text-slate-600">Start date (UK)
          <Input data-testid="wa-start" type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1" /></label>
        <label className="text-xs font-semibold text-slate-600">End date (UK, optional)
          <Input data-testid="wa-end" type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1" /></label>
        <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Note
          <Textarea data-testid="wa-note" value={f.note} onChange={(e) => set("note", e.target.value)} className="mt-1 min-h-[50px]" /></label>
        <div className="sm:col-span-2"><Button data-testid="wa-add" disabled={busy} onClick={create} className="bg-emerald-700 hover:bg-emerald-800 gap-1"><Plus size={15} /> Add assignment</Button></div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-1.5"><UsersThree size={17} /> Assignments</h3>
        <label className="flex items-center gap-1.5 text-xs text-slate-600"><input data-testid="wa-include-ended" type="checkbox" checked={includeEnded} onChange={(e) => setIncludeEnded(e.target.checked)} /> Show ended</label>
      </div>
      <div className="flex flex-col gap-2" data-testid="wa-list">
        {rows.length === 0 && <p className="text-sm text-slate-400 italic">No assignments yet.</p>}
        {rows.map((w) => (
          <div key={w.id} data-testid={`wa-row-${w.id}`} className="bg-white border border-slate-200 rounded-lg px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-slate-800 text-sm truncate">{w.person_name}</div>
              <div className="text-xs text-slate-500 truncate">{w.client_name}{w.project_name ? ` · ${w.project_name}` : " · whole client"}
                {(w.start_date || w.end_date) && <span> · {w.start_date ? new Date(w.start_date).toLocaleDateString("en-GB") : "—"} → {w.end_date ? new Date(w.end_date).toLocaleDateString("en-GB") : "open"}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge(w.effective_status)}`}>{w.effective_status}</span>
              {w.status === "active" && <Button data-testid={`wa-end-${w.id}`} onClick={() => end(w.id)} variant="ghost" className="h-8 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50 gap-1"><Prohibit size={14} /> End</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
